import { useSetAtom } from 'jotai';
import { Suspense, useEffect } from 'react';
import { setAtomsToHydrateAtom } from '@/atoms/hydration';
import { showLegendAtom } from '@/atoms/visualisation';
import { metricKeyHydrateAtom } from '@/consts/atomHydration';
import { entityVisualisationComponents } from '@/consts/widget';
import { useSetGameTeamData } from '@/hooks/useSetGameTeamData';
import { useWidgetNavigationPath } from '@/hooks/useWidgetNavigationPath';
import { StatusTranslationKey } from '@/types/status';
import { AtomsToHydrate } from '@/types/userConfigs';
import { DashboardWidgetVis } from '@/types/widget';
import { getDisplayAtomsToHydrate } from '@/utils/atoms/atomHydration';
import { ContentState } from '../contentState/ContentState';
import { DashboardWidget } from './DashboardWidget';
import { DashboardWidgetScopeProvider } from './DashboardWidgetScopeProvider';

const WidgetWrapperWithData = ({
  metric,
  entity,
  entityId,
  vis,
  widgetId,
  titleSettings,
  displaySettings,
  teamType,
  showLegend = false,
}: DashboardWidgetVis) => {
  const navigationPath = useWidgetNavigationPath(
    entity,
    entityId,
    vis,
    metric,
    displaySettings.radarTemplateId,
    displaySettings,
  );
  const Component = entityVisualisationComponents[entity][vis]!;
  useSetGameTeamData(teamType);

  const displayAtomsToHydrate = getDisplayAtomsToHydrate(displaySettings);
  const setAtomsToHydrate = useSetAtom(setAtomsToHydrateAtom);
  const setShowLegendAtom = useSetAtom(showLegendAtom);

  const atomsToHydrate = metric
    ? ([[metricKeyHydrateAtom, metric], ...displayAtomsToHydrate] as AtomsToHydrate)
    : displayAtomsToHydrate;

  useEffect(() => {
    setAtomsToHydrate(atomsToHydrate);
    setShowLegendAtom(showLegend);
  }, []);

  return (
    <DashboardWidget vis={vis} titleSettings={titleSettings} navigationPath={navigationPath}>
      <Component isWidget id={widgetId} />
    </DashboardWidget>
  );
};

export const DashboardWidgetWrapper = (props: DashboardWidgetVis) => {
  const { widgetId, metric } = props;

  return (
    <Suspense fallback={<ContentState status={StatusTranslationKey.LOADING} />}>
      <DashboardWidgetScopeProvider widgetId={widgetId} metric={metric}>
        <WidgetWrapperWithData {...props} />
      </DashboardWidgetScopeProvider>
    </Suspense>
  );
};
